import React, { useCallback, useEffect, useState } from 'react';
import { Box, Heading, Table, Tbody, Td, Text, Th, Thead, Tr, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { AppCard, TableLoader, TablePagination } from '../../../../components/ui';
import { listEquipmentKyc } from '../../../../repositories/equipmentKyc.repository';
import { formatDateTimeDisplay } from '../../../../utils/personName';
import { KycActionButtons, KycListFilters, parseKycSort, useKycCrud } from './kycUi';

export default function EquipmentKycListPage({ embedded = false } = {}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [submittedFrom, setSubmittedFrom] = useState('');
  const [submittedTo, setSubmittedTo] = useState('');
  const [sortValue, setSortValue] = useState('submittedAt:DESC');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { sortBy, sortDir } = parseKycSort(sortValue);
      const result = await listEquipmentKyc({
        page,
        pageSize,
        search: debouncedSearch || undefined,
        sortBy,
        sortDir,
        submittedFrom: submittedFrom || undefined,
        submittedTo: submittedTo || undefined,
      });
      setRows(result.items || []);
      setTotal(result.total || 0);
    } catch (err) {
      toast({ title: err?.message || 'Failed to load', status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, sortValue, submittedFrom, submittedTo, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const crud = useKycCrud({ onSaved: () => load(), onDeleted: () => load() });

  return (
    <Box>
      {!embedded ? (
        <Heading size="md" mb={4}>
          Equipment KYC
        </Heading>
      ) : null}
      <KycListFilters
        search={search}
        onSearch={(value) => {
          setSearch(value);
          setPage(1);
        }}
        submittedFrom={submittedFrom}
        submittedTo={submittedTo}
        onFrom={(e) => {
          setSubmittedFrom(e.target.value);
          setPage(1);
        }}
        onTo={(e) => {
          setSubmittedTo(e.target.value);
          setPage(1);
        }}
        sortValue={sortValue}
        onSort={(value) => {
          setSortValue(value);
          setPage(1);
        }}
        total={total}
        onClear={() => {
          setSearch('');
          setSubmittedFrom('');
          setSubmittedTo('');
          setSortValue('submittedAt:DESC');
          setPage(1);
        }}
      />
      <AppCard p={0} overflow="hidden">
        {loading ? (
          <TableLoader />
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>#</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>RC</Th>
                <Th>Aadhaar</Th>
                <Th>Submitted</Th>
                {crud.canEdit || crud.canDelete ? <Th textAlign="right">Actions</Th> : null}
              </Tr>
            </Thead>
            <Tbody>
              {rows.length ? (
                rows.map((row, index) => (
                  <Tr
                    key={row.id}
                    cursor="pointer"
                    onClick={() => navigate(`/admin/equipments/public-kyc/${row.id}`)}
                    _hover={{ bg: 'bg.input' }}
                  >
                    <Td fontWeight="600">{(page - 1) * pageSize + index + 1}</Td>
                    <Td>{row.email}</Td>
                    <Td>{row.phone}</Td>
                    <Td>{row.rcCount ?? 0}</Td>
                    <Td>{row.aadhaarCount ?? 0}</Td>
                    <Td>{formatDateTimeDisplay(row.submittedAt)}</Td>
                    {crud.canEdit || crud.canDelete ? (
                      <Td>
                        <KycActionButtons
                          canEdit={crud.canEdit}
                          canDelete={crud.canDelete}
                          onEdit={() => crud.setEditRow(row)}
                          onDelete={() => crud.setDeleteRow(row)}
                        />
                      </Td>
                    ) : null}
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={crud.canEdit || crud.canDelete ? 7 : 6}>
                    <Text py={6} textAlign="center" color="text.secondary">
                      No submissions yet
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        )}
      </AppCard>
      <TablePagination
        page={page}
        pageSize={pageSize}
        totalCount={total}
        totalPages={Math.max(1, Math.ceil((total || 0) / pageSize))}
        onPageChange={setPage}
      />
      {crud.dialogs}
    </Box>
  );
}
